import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import cx from 'clsx';
import styles from './index.module.scss';
import { AntdSelect, AutoSizeTextarea, Button, Input, Loading, Tips } from '@/components';
import { arrayToObject, objectToArray, urlRegex } from '../../utils';
import { ErrorMessage } from '@hookform/error-message';
import { IconClose } from '@/assets/quest';
import { debounce, isNumber } from 'lodash';
import { useActionLoading } from '../ActionSelect/state';
import { useValidateApiUrl, useValidateSheetUrl } from './hooks';
import { useDebounceState } from '@/hooks';
const { Option } = AntdSelect;

const GUIDE_DOC_URL =
  'https://docs.cloud.debank.com/en/official-account/quest/rest-api-action-integration-spec';

export const CustomApi = () => {
  const {
    control,
    watch,
    formState: { errors },
    clearErrors,
    setValue,
  } = useFormContext();
  const handleClickLink = useCallback(e => {
    e.stopPropagation();
    window.open(GUIDE_DOC_URL);
  }, []);
  const type = watch('type');

  const handleTypeChange = useCallback(() => {
    // 修改type后重置data
    setValue('data.data_url', '');
    setValue('data.title', '');
    setValue('data.desc', '');
    setValue('data.related_link', '');
    setValue('data._headers', undefined);
    clearErrors();
  }, [clearErrors, setValue]);

  return (
    <>
      <div className={styles.formItem}>
        <div className={styles.labelContainer}>
          <div className={cx(styles.label, styles.isRequired)}>title</div>
        </div>
        <Controller
          control={control}
          name="data.title"
          render={({ field, fieldState }) => (
            <Input
              className={styles.input}
              placeholder="e.g. Rabby transaction users"
              value={field?.value}
              onChange={field?.onChange}
              maxLength={30}
              showLength
            />
          )}
          rules={{
            required: true,
          }}
        />
      </div>
      <div className={styles.formItem}>
        <div className={styles.labelContainer}>
          <div className={cx(styles.label, styles.isRequired)}>Interaction description</div>
        </div>
        <Controller
          control={control}
          name="data.desc"
          render={({ field, fieldState }) => (
            <AutoSizeTextarea
              className={styles.input}
              placeholder="Describe how to interact... "
              value={field?.value}
              onChange={field?.onChange}
              minHeight={20}
              maxHeight={160}
              showLength
              maxLength={120}
              error={!!fieldState.error}
              rows={1}
            />
          )}
          rules={{
            required: true,
          }}
        />
      </div>
      <div className={styles.formItem}>
        <div className={styles.labelContainer}>
          <div className={cx(styles.label, styles.isRequired)}>Credential source</div>
        </div>
        <Controller
          control={control}
          name="type"
          render={({ field, fieldState }) => {
            return (
              <AntdSelect
                className={styles.select}
                value={field?.value}
                onChange={value => {
                  field?.onChange(value);
                  handleTypeChange();
                }}
                optionLabelProp="label">
                <Option className={styles.dataTypeOption} value="google_sheet" label="Google Sheet">
                  <div className={styles.title}>Google Sheet</div>
                  <div className={styles.desc}>
                    Holder list will be synced from your Google Sheet every 12 hours.
                  </div>
                </Option>
                <Option className={styles.dataTypeOption} value="rest_api" label="Rest API">
                  <div className={styles.title}>Rest API</div>
                  <div className={styles.desc}>
                    Use API to to update holder list.{' '}
                    <span className={styles.link} onClick={handleClickLink}>
                      Learn how to update via the Rest API
                    </span>
                  </div>
                </Option>
              </AntdSelect>
            );
          }}
        />
        {type === 'rest_api' ? (
          <div className={styles.link} onClick={handleClickLink}>
            Learn how to update via the Rest API
          </div>
        ) : null}
      </div>
      {type === 'google_sheet' ? <GoogleSheetField /> : <RestApiField />}
      <div className={styles.formItem}>
        <div className={styles.labelContainer}>
          <div className={cx(styles.label, styles.isRequired)}>Related link</div>
        </div>
        <Controller
          control={control}
          name="data.related_link"
          render={({ field, fieldState }) => (
            <Input
              className={styles.input}
              placeholder="The link users can visit and complete the task"
              value={field?.value}
              onChange={field?.onChange}
              error={!!fieldState.error}
            />
          )}
          rules={{
            required: true,
            pattern: {
              value: urlRegex,
              message: 'Please input a valid Link.',
            },
          }}
        />
        <ErrorMessage
          errors={errors}
          name={`data.related_link`}
          render={({ message }) => {
            return <div className={styles.errorMessage}>{message}</div>;
          }}
        />
      </div>
    </>
  );
};

const GoogleSheetField = () => {
  const {
    control,
    formState: { errors },
    setError,
    watch,
  } = useFormContext();
  const url = watch('data.data_url');
  const [, setActionLoading] = useActionLoading();
  const [totalCount, setTotalCount] = useState<number>();
  const sheetUrlRef = useRef<string>();

  const { mutateAsync: validateSheetUrl, isLoading } = useValidateSheetUrl();

  useEffect(() => {
    setActionLoading(isLoading);
  }, [isLoading, setActionLoading]);

  useEffect(() => {
    if (!url) {
      setActionLoading(false);
    }
  }, [setActionLoading, url]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validateUrl = useCallback(
    debounce(async (url: string) => {
      if (url === sheetUrlRef.current) {
        return;
      }
      sheetUrlRef.current = url;
      const res = await validateSheetUrl({ dataUrl: url });
      if (res?.is_valid) {
        setTotalCount(res?.total_count);
        return true;
      }
      setTotalCount(undefined);
      setError('data.data_url', {
        message: res?.reason,
      });
      return;
    }, 500),
    [validateSheetUrl],
  );

  return (
    <div className={styles.formItem}>
      <div className={styles.labelContainer}>
        <div className={cx(styles.label, styles.isRequired)}>Google Sheet URL</div>
        <Tips
          placement="right"
          title="Data will be extracted from the first column of the first sheet in your Google Sheet, with a limit of up to 10,000 entries."
        />
      </div>
      <Controller
        control={control}
        name="data.data_url"
        render={({ field, fieldState }) => (
          <Input
            className={styles.input}
            placeholder="Paste Google Sheet URL link(Set shared by link before)"
            value={field?.value}
            onChange={val => {
              field?.onChange(val);
              setTotalCount(undefined);
              setActionLoading(true);
              if (!val) {
                sheetUrlRef.current = '';
              }
            }}
            error={!!fieldState.error}
          />
        )}
        rules={{
          required: true,
          validate: validateUrl!,
        }}
      />
      {isNumber(totalCount) && !isLoading ? (
        <div className={styles.countTip}>Imported {totalCount} entries.</div>
      ) : null}
      {isLoading ? (
        <div className={styles.validateLoading}>
          <Loading /> while data is loading
        </div>
      ) : null}
      <ErrorMessage
        errors={errors}
        name={`data.data_url`}
        render={({ message }) => {
          return <div className={styles.errorMessage}>{message}</div>;
        }}
      />
    </div>
  );
};

const RestApiField = () => {
  const {
    control,
    watch,
    setError,
    setValue,
    getValues,
    formState: { errors },
    trigger,
    clearErrors,
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'data._headers', // 提交给后端的结构不一样，用_headers做临时变量
  });
  const url = watch('data.data_url');
  const _headers = useWatch({ control, name: 'data._headers' });
  const [debouncedHeaders, setDebouncedHeaders] = useDebounceState();

  const validHeaders = useMemo(() => {
    const headers = _headers?.filter(i => i.key && i.value);
    if (!headers?.length) {
      setDebouncedHeaders(undefined);
      return undefined;
    }
    const validHeaders = arrayToObject(headers);
    setDebouncedHeaders(validHeaders);
    return validHeaders;
  }, [_headers, setDebouncedHeaders]);
  const [, setActionLoading] = useActionLoading();

  useEffect(() => {
    if (validHeaders) {
      setValue('data.headers', validHeaders);
    }
  }, [setValue, validHeaders]);

  useEffect(() => {
    const initHeaders = getValues('data.headers');
    // 初始值回填
    if (initHeaders) {
      setValue('data._headers', objectToArray(initHeaders));
    }
  }, [getValues, setValue]);

  useEffect(() => {
    // 修改headers后重新触发校验
    if (debouncedHeaders && url) {
      trigger('data.data_url');
    }
  }, [trigger, url, debouncedHeaders]);

  const { mutateAsync: validateApiUrl, isLoading } = useValidateApiUrl();

  useEffect(() => {
    setActionLoading(isLoading);
  }, [isLoading, setActionLoading]);

  useEffect(() => {
    if (!url) {
      setActionLoading(false);
    }
  }, [setActionLoading, url]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validateUrl = useCallback(
    debounce(async (url: string) => {
      const res = await validateApiUrl({ dataUrl: url, headers: validHeaders });
      if (res?.is_valid) {
        clearErrors('data.data_url');
        return true;
      }
      setError('data.data_url', {
        message: res?.reason,
      });
      return;
    }, 500),
    [validateApiUrl, validHeaders],
  );

  return (
    <>
      <div className={styles.formItem}>
        <div className={styles.labelContainer}>
          <div className={cx(styles.label, styles.isRequired)}>Endpoint</div>
        </div>
        <Controller
          control={control}
          name="data.data_url"
          render={({ field, fieldState }) => (
            <Input
              className={styles.input}
              placeholder="e.g https://api.example.com/"
              value={field?.value}
              onChange={field?.onChange}
              error={!!fieldState.error}
            />
          )}
          rules={{
            required: true,
            validate: validateUrl!,
          }}
        />
        <ErrorMessage
          errors={errors}
          name={`data.data_url`}
          render={({ message }) => {
            return <div className={styles.errorMessage}>{message}</div>;
          }}
        />
      </div>
      <div className={styles.formItem}>
        <div className={styles.labelContainer}>
          <div className={styles.label}>Header</div>
        </div>
        <div className={styles.headerFieldContainer}>
          <div className={styles.header}>
            <div>Key</div>
            <div>Value</div>
          </div>
          {fields?.map((field, index) => (
            <div className={styles.headerInputContainer} key={field.id}>
              <Controller
                control={control}
                name={`data._headers.${index}.key`}
                render={({ field, fieldState }) => (
                  <Input
                    className={styles.headerInput}
                    placeholder="e.g authorization"
                    value={field?.value}
                    onChange={field?.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name={`data._headers.${index}.value`}
                render={({ field, fieldState }) => (
                  <Input
                    className={styles.headerInput}
                    placeholder="e.g authorization"
                    value={field?.value}
                    onChange={field?.onChange}
                  />
                )}
              />
              <IconClose className={styles.closeIcon} onClick={() => remove(index)} />
            </div>
          ))}
          <Button
            type="primary"
            className={styles.addBtn}
            disabled={fields.length >= 10}
            onClick={() => append({ key: undefined, value: undefined })}>
            add
          </Button>
        </div>
      </div>
    </>
  );
};
